import {ActionTree} from 'vuex';
import axios, {AxiosError, AxiosResponse} from "axios";
import * as moment from 'moment';
import joinColumnValues from "../ts/joinColumnValues";
import User from "../ts/User";
import AjaxError from "../../../components/helpers/AjaxError";

export const actions: ActionTree<any, any> = {

    /**
     * Get all users
     *
     * @param {any} state
     * @param {any} getters
     * @param {any} commit
     * @param {any} dispatch
     * @returns {Promise<any>}
     */

    getUsers: ({state, getters, commit, dispatch}, forceUpdate: boolean) => {

        if (forceUpdate) {
            state.usersCacheExpires = null;
        }

        // Pull from store if loaded and cache hasn't expired
        if (state.usersCacheExpires === null || moment().isAfter(state.usersCacheExpires)) {

            commit('setUsersLoading', true);

            return new Promise((resolve, reject) => {

                commit('setUsersLoading', true);

                axios.get('/users')

                    .then(({data}) => {

                        joinColumnValues("roles", "name", data.users);

                        commit('clearUsers');

                        let users: Array<User> = [];

                        data.users.forEach((user: User) => {
                            const userObject = Object.assign({}, user);
                            users.push(userObject);
                        });

                        commit('setUsers', users);
                        commit('setUsersLoading', false);
                        resolve(state.users);
                    })
                    .catch(error => {

                        console.log(error);

                        commit('setUsersLoading', false);
                        commit('setError', new AjaxError(error.message, error.response.status));
                        reject(error);
                    });

            });
        }
    },

    /**
     * Load user roles to the store
     *
     * @param {any} state
     * @param {any} getters
     * @param {any} commit
     * @param {any} dispatch
     * @param userId
     * @returns {Promise<any>}
     */

    loadUserRoles({state, getters, commit, dispatch}, userId) {

        return new Promise((resolve, reject) => {
            let found = state.users.find((user: User) => {
                return user.id === userId;
            });
            resolve(found.roles);
        });
    },

    /**
     * Persist roles
     *
     * @param {any} state
     * @param {any} getters
     * @param {any} commit
     * @param {any} dispatch
     * @param {any} userId
     * @param {any} checked
     * @returns {Promise<any>}
     */

    saveUserRoles({state, getters, commit, dispatch, rootState}, {userId, roles}) {

        let oldUser = getters.getUserClone(userId);

        commit('setUserRole', {userId: userId, roles: roles});

        return new Promise((resolve, reject) => {

            axios.put('/user/' + userId + '/roles', {'roles': roles})
                .then(response => {

                    // If modifying user's own permissions refresh access list
                    if (userId === rootState.user.id) {
                        dispatch('getACL', true, {root: true});
                    }
                    resolve(response);
                })
                .catch(error => {
                    // Restore original user's roles immediately on error
                    commit('setUserRole', {userId: oldUser.id, roles: oldUser.roles});
                    reject(error);
                });
        });
    },

    /**
     * Invite user to join the team
     *
     * @param {any} state
     * @param {any} getters
     * @param {any} commit
     * @param {any} dispatch
     * @param {any} form
     * @returns {Promise<any>}
     */

    inviteUser({state, getters, commit, dispatch}, data) {

        return new Promise((resolve, reject) => {

            axios.post('/user/invitation', data)
                .then((response: AxiosResponse) => {
                    resolve(response.data);
                })
                .catch((error: AxiosError) => {
                    reject(error.response);
                });
        });
    },

    /**
     * Get list of outstanding user invitations
     *
     * @param {any} state
     * @param {any} getters
     * @param {any} commit
     * @param {any} dispatch
     * @param {any} form
     * @returns {Promise<any>}
     */

    getPendingInvitations({state, getters, commit, dispatch}) {

        commit("setPendingInvitationsLoading", true);

        return new Promise((resolve, reject) => {

            // Get all roles and permissions
            axios.get('/user/invitations')
                .then((response) => {
                    commit("setPendingInvitations", response.data.invitations);
                    commit("setPendingInvitationsLoading", false);
                    resolve(response);
                })
                .catch(error => {
                    commit("setPendingInvitationsLoading", false);
                    reject(error);
                });
        });
    },

    /**
     * Cancel user invitation
     *
     * @param {any} state
     * @param {any} getters
     * @param {any} commit
     * @param {any} dispatch
     * @param {any} id
     * @returns {Promise<any>}
     */

    cancelPendingInvitation({state, getters, commit, dispatch}, id) {

        // Clone original
        let oldInvites = Object.assign({}, ...state.pendingInvitations);

        commit("cancelPendingInvitation", id);

        return new Promise((resolve, reject) => {
            axios.delete('/user/invitation/' + id)
                .then((response) => {
                    resolve(response);
                })
                .catch(error => {
                    commit("setPendingInvitations", oldInvites);
                    reject(error);
                })
        });
    },

    /**
     * Get roles
     *
     * @param {any} state
     * @param {any} getters
     * @param {any} commit
     * @param {any} dispatch
     * @param force
     * @returns {Promise<any>}
     */
    getRoles: ({state, getters, commit, dispatch}, force: boolean) => {

        if (!force && state.rolesLoadedTime &&
            state.rolesLoadedTime > moment().subtract(15, 'minutes').format('X')) {

            return new Promise((resolve, reject) => {
                resolve(state.roles);
            });
        }

        return new Promise((resolve, reject) => {

            commit('setRolesLoading', true);

            axios.get('/roles')
                .then(({data}) => {
                    commit('setRoles', data.roles);
                    commit('setPermissions', data.permissions);
                    commit('setRolesLoading', false);
                    resolve(data);

                })
                .catch(error => {
                    commit('setRolesLoading', false);
                    reject(error)
                });
        });
    },

    updateUser: ({state, getters, commit, dispatch}, data) => {

        commit('setUserUpdating', true);

        return new Promise((resolve, reject) => {

            axios.put('/user/' + data.id, data)

                .then(({data}: AxiosResponse) => {

                    commit('updateUsers', data.user);
                    commit('setUserUpdating', false);
                    resolve(data.user);
                })
                .catch(({response}) => {
                    commit('setUserUpdating', false);
                    reject(response);
                });
        });
    },

    /**
     * Delete user
     *
     * @param state
     * @param getters
     * @param commit
     * @param dispatch
     * @param id
     */
    deleteUser({state, getters, commit, dispatch}, {id, assignTo}) {

        return new Promise((resolve, reject) => {

            axios.delete('/user/' + id + '?assign_to=' + assignTo)
                .then((response) => {
                    commit('deleteUser', id);
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                })
        });
    },

    createRole({state, getters, commit, dispatch}, data) {

        return new Promise((resolve, reject) => {

            axios.post('/role', data)
                .then((response: AxiosResponse) => {
                    commit('createRole', response.data.data.role);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    updateRole({state, getters, commit, dispatch}, {id, name, permissions}) {

        return new Promise((resolve, reject) => {

            const data = {
                name,
                permissions,
            }

            axios.put('/role/' + id, data)
                .then((response: AxiosResponse) => {
                    commit('updateRole', response.data.data.role);
                    resolve(response);
                })
                .catch((error: AxiosError) => {
                    reject(error);
                });
        });
    },

    deleteRole({state, getters, commit, dispatch}, {id}) {

        return new Promise((resolve, reject) => {

            axios.delete('/role/' + id)
                .then((response) => {
                    commit('deleteRole', id);
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                })
        });
    },
};
